import { useEffect, useRef, useMemo, useCallback } from 'react';
// Note: d3 import removed — pure tree layout, no force simulation needed

/**
 * Tree layout hook for React Flow.
 *
 * Positions nodes in a top-down tree: siblings spread horizontally,
 * children below their parent.
 *
 * Design: layout is triggered IMPERATIVELY via the returned `applyTreeLayout`
 * function — call it after adding a node or edge. It is NOT reactive on the
 * nodes/edges arrays, which prevents the render-loop that occurs when
 * setNodes() inside a useEffect causes a re-render that re-fires the effect.
 *
 * @param {Function} onPositionChange    - Callback to apply new positions: (positions) => void
 * @param {Object}   options             - Optional overrides for spacing
 * @returns {{ applyTreeLayout: (nodes, edges, opts?) => void }}
 *
 * applyTreeLayout optional third arg:
 *   { newNodeId, rfInstance } — if provided, after layout the viewport is
 *   fitted to show the new leaf node and up to 2 of its ancestors.
 */
const useTreeLayout = (onPositionChange, options = {}) => {
  const { treeHorizontalSpacing = 360, treeVerticalSpacing = 60 } = options;
  const layoutOptions = useMemo(() => ({
    treeHorizontalSpacing,
    treeVerticalSpacing, // gap between bottom of parent node and top of child node
  }), [treeHorizontalSpacing, treeVerticalSpacing]);

  const onPositionChangeRef = useRef(onPositionChange);
  useEffect(() => {
    onPositionChangeRef.current = onPositionChange;
  }, [onPositionChange]);

  // Pure layout calculation — anchors root at canvas origin (0, 100).
  // Vertical spacing is based on each node's measured height (node.measured.height)
  // when available, falling back to a default. This prevents long connectors when
  // nodes have variable content heights.
  const calculatePositions = useCallback((nodes, edges) => {
    if (!nodes.length) return [];

    const { treeHorizontalSpacing: hSpacing, treeVerticalSpacing: vGap } = layoutOptions;

    // Default heights per node type (used before ReactFlow measures them)
    const defaultHeightByType = {
      imageNode: 380,   // image nodes are tall
      fileNode: 80,
      promptNode: 40,   // prompt chips are tiny
      textNode: 160,
      googleSheetsNode: 200,
      mapNode: 300,
    };

    // Build height lookup from ReactFlow's measured dimensions (available after first render)
    const heightOf = {};
    nodes.forEach(n => {
      heightOf[n.id] = (n.measured && n.measured.height)
        ? n.measured.height
        : (defaultHeightByType[n.type] ?? 180);
    });

    const childMap = {};
    nodes.forEach(n => { childMap[n.id] = []; });
    edges.forEach(e => {
      if (childMap[e.source] !== undefined) {
        childMap[e.source].push(e.target);
      }
    });

    const hasParent = new Set(edges.map(e => e.target));
    let roots = nodes.filter(n => !hasParent.has(n.id));
    if (roots.length === 0) roots = [nodes[0]];

    const positions = [];

    // parentY = top-left y of the parent node
    const positionNode = (nodeId, x, parentY, parentHeight, index, siblingCount) => {
      const nodeX = x + (index - (siblingCount - 1) / 2) * hSpacing;
      const nodeY = parentY + parentHeight + vGap;
      positions.push({ id: nodeId, position: { x: nodeX, y: nodeY } });
      const children = childMap[nodeId] || [];
      children.forEach((childId, i) => {
        positionNode(childId, nodeX, nodeY, heightOf[nodeId], i, children.length);
      });
    };

    // Roots start at y=100
    roots.forEach((root, i) => {
      const rootX = (i - (roots.length - 1) / 2) * hSpacing;
      positions.push({ id: root.id, position: { x: rootX, y: 100 } });
      const children = childMap[root.id] || [];
      children.forEach((childId, j) => {
        positionNode(childId, rootX, 100, heightOf[root.id], j, children.length);
      });
    });

    return positions;
  }, [layoutOptions]);

  const getTopologySignature = useCallback((nodes, edges) => {
    const nodeIds = nodes.map(n => n.id).sort().join(',');
    const edgeIds = edges.map(e => `${e.source}->${e.target}`).sort().join(',');
    return `${nodeIds}|${edgeIds}`;
  }, []);

  const lastTopologyRef = useRef('');

  /**
   * Apply tree layout then optionally focus the viewport on the new node.
   *
   * @param {Array}  nodes
   * @param {Array}  edges
   * @param {Object} [focusOpts]
   * @param {string} [focusOpts.newNodeId]  - ID of newly added node (leaf to focus on)
   * @param {Object} [focusOpts.rfInstance] - ReactFlow instance for fitView
   */
  const applyTreeLayout = useCallback((nodes, edges, focusOpts = {}) => {
    if (!nodes || nodes.length < 2) return;

    const sig = getTopologySignature(nodes, edges);
    if (sig === lastTopologyRef.current) return;
    lastTopologyRef.current = sig;

    const newPositions = calculatePositions(nodes, edges);
    if (!newPositions.length || !onPositionChangeRef.current) return;

    onPositionChangeRef.current(newPositions);

    // After positions applied, fit viewport to new leaf + up to 2 ancestors
    const { newNodeId, rfInstance } = focusOpts;
    if (!newNodeId || !rfInstance) return;

    // Build parent lookup from edges
    const parentOf = {};
    edges.forEach(e => { parentOf[e.target] = e.source; });

    // Collect new node + up to 2 ancestors (excluding hidden root)
    const focusIds = new Set();
    let cursor = newNodeId;
    for (let i = 0; i < 3 && cursor; i++) {
      focusIds.add(cursor);
      cursor = parentOf[cursor];
    }

    // Small delay to allow React to flush position updates before fitView
    setTimeout(() => {
      rfInstance.fitView({
        nodes: [...focusIds].map(id => ({ id })),
        padding: 0.25,
        duration: 400,
      });
    }, 50);
  }, [calculatePositions, getTopologySignature]);

  return { applyTreeLayout };
};

export default useTreeLayout;
